import React from 'react';
import styled from 'styled-components';
import { Color, Grid, Type } from '../../app/StyleGuide';
import { SmallPrimaryButton } from '../../components/Buttons';
import { TrainingPoster } from '../../components/TrainingElements';
import WhiteLabel from '../../filters/WhiteLabel';
import apiClient from '../../services/apiClient';
import trainingService from '../../services/trainingService';
import windowService from '../../services/windowService';
import CourseInformation from './CourseInformation';
import CourseSteps from './CourseSteps';
import { type Report } from './models/course';
import { type PathwayCourse } from './models/pathway';
import { StepsMenuProgressBar } from './components/StepsMenuProgressBar';

const CourseHeader = styled.div<{ isActive?: boolean }>`
  color: ${Type.Color.dark};
  padding: ${Grid._4};
  position: relative;
  border-top: 1px solid ${Color.Gray._30};
  background-color: ${props => (WhiteLabel.isGrid() && props.isActive ? Color.Primary._05 : 'transparent')};
  &:first-child {
    border-top: 0;
  }
  &:last-child {
    border-bottom: 1px solid ${Color.Gray._30};
  }
`;

const ToggleIcon = styled.div`
  .fas {
    font-size: ${Type.Scale._4};
  }
`;

const StyledSmallPrimaryButton = styled(SmallPrimaryButton)`
  &:not(:last-child) {
    margin-bottom: ${Grid._4};
  }
`;

const CourseName = styled.div`
  display: flex;
  align-items: center;
  gap: ${Grid._3};

  h4 {
    flex: 1;
    font-size: ${Type.Scale._3};
    font-weight: ${Type.Weight.semibold};
    &:hover {
      text-decoration: underline;
    }
  }
`;

const StyledTrainingPoster = styled(TrainingPoster)`
  max-width: 100px;
`;

type ViewReportButtonProps = {
  fileName: string;
  filePath: string;
};
const ViewReportButton = ({ fileName, filePath }: ViewReportButtonProps) => {
  const encodedFileName = encodeURIComponent(fileName.replace(/ /gi, '_'));
  const encodedFilePath = encodeURIComponent(filePath);

  const openReport = () =>
    windowService.openUrl(
      `${apiClient.getConfig().baseURL}/v1/files?fileName=${encodedFileName}&filePath=${encodedFilePath}`
    );

  return (
    <StyledSmallPrimaryButton onClick={openReport}>
      <i className="fa fa-chart-bar" /> View Report
    </StyledSmallPrimaryButton>
  );
};

type Training = {
  workflowName: string;
};

type CourseMenuProps = {
  training: Training;
  course: PathwayCourse;
  isCurrentCourse: boolean;
  courseIndex: number;
  isCourseExpanded: boolean;
  toggleIsCourseExpanded: () => void;
};

export default function CourseMenu(props: CourseMenuProps) {
  const { training, course, isCurrentCourse, courseIndex, isCourseExpanded, toggleIsCourseExpanded } = props;
  const isExpanded = isCourseExpanded;
  const numCompletedSteps = course.tasks.filter(step => step.status === 'complete').length;
  const isInstanceDetail = course._type === 'CourseInstanceDetail';
  const chevronDirection = isExpanded ? 'up' : 'down';

  const shouldShowViewReport = (report: Report) =>
    report?.reportId && report?.id === 'CPCACandidate' && report?.visibility === 'PUBLIC';

  return (
    <>
      <CourseHeader isActive={isCurrentCourse} style={{ cursor: 'pointer' }} onClick={() => toggleIsCourseExpanded()}>
        <CourseName>
          {(isCurrentCourse || isExpanded) && course.posterImage && (
            <StyledTrainingPoster src={trainingService.getTrainingImageUrl(course.posterImage)} />
          )}
          <h4 data-qa-hook="courseName">{course.name}</h4>
          <ToggleIcon>
            <i className={'fas fa-chevron-' + chevronDirection}></i>
          </ToggleIcon>
        </CourseName>
        {course.reports &&
          course.reports.map(
            report =>
              shouldShowViewReport(report) && (
                <ViewReportButton key={report.reportId} fileName={training.workflowName} filePath={report.reportId} />
              )
          )}
        {!isExpanded && isInstanceDetail && <StepsMenuProgressBar progress={course.progress.percent} />}
        {isExpanded && isInstanceDetail && (
          <CourseInformation course={course} numCompletedSteps={numCompletedSteps} progress={course.progress.percent} />
        )}
        {isExpanded && !isInstanceDetail && (
          <p style={{ color: Type.Color.medium }}>
            Upcoming {WhiteLabel.labels.course} will be unlocked upon completion of previous {WhiteLabel.labels.course}
          </p>
        )}
      </CourseHeader>
      {isExpanded && <CourseSteps course={course} courseIndex={courseIndex} isCurrentCourse={isCurrentCourse} />}
    </>
  );
}
